import { collection, addDoc, serverTimestamp, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { hash } from './encryptionService';
import { DEMO_AUDIT_LOGS } from '../contexts/DemoContext';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

/**
 * Audit Service for HIPAA Compliance
 *
 * Logs all access to PHI (Protected Health Information) for compliance.
 * Audit logs are immutable and can only be created (not updated or deleted).
 */

export const AUDIT_ACTIONS = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  DOWNLOAD: 'download',
  UPLOAD: 'upload',
  EXPORT: 'export',
  LOGIN: 'login',
  LOGOUT: 'logout',
  ACCESS_DENIED: 'access_denied'
};

export const RESOURCE_TYPES = {
  PATIENT: 'patient',
  CASE: 'case',
  PHASE: 'phase',
  PHOTO: 'photo',
  USER: 'user',
  PRACTICE: 'practice',
  VERSION: 'version'
};

export const logAudit = async ({
  practiceId, userId, action, resourceType, resourceId, details = {}
}) => {
  if (IS_DEMO) return 'demo-log';

  try {
    const ipAddress = await getHashedIP();
    const auditLog = {
      practiceId, userId, action, resourceType, resourceId,
      timestamp: serverTimestamp(),
      ipAddress, userAgent: navigator.userAgent, details
    };
    const docRef = await addDoc(collection(db, 'auditLogs'), auditLog);
    return docRef.id;
  } catch (error) {
    console.error('Failed to log audit event:', error);
    return null;
  }
};

export const logPatientAccess = (practiceId, userId, patientId, action = AUDIT_ACTIONS.READ) => {
  return logAudit({ practiceId, userId, action, resourceType: RESOURCE_TYPES.PATIENT, resourceId: patientId });
};

export const logCaseAccess = (practiceId, userId, caseId, action = AUDIT_ACTIONS.READ) => {
  return logAudit({ practiceId, userId, action, resourceType: RESOURCE_TYPES.CASE, resourceId: caseId });
};

export const logPhotoAccess = (practiceId, userId, photoId, action = AUDIT_ACTIONS.READ) => {
  return logAudit({ practiceId, userId, action, resourceType: RESOURCE_TYPES.PHOTO, resourceId: photoId });
};

export const logPhotoUpload = (practiceId, userId, photoId, details = {}) => {
  return logAudit({ practiceId, userId, action: AUDIT_ACTIONS.UPLOAD, resourceType: RESOURCE_TYPES.PHOTO, resourceId: photoId, details });
};

export const logPhotoDownload = (practiceId, userId, photoId, details = {}) => {
  return logAudit({ practiceId, userId, action: AUDIT_ACTIONS.DOWNLOAD, resourceType: RESOURCE_TYPES.PHOTO, resourceId: photoId, details });
};

export const logLogin = (practiceId, userId) => {
  return logAudit({ practiceId, userId, action: AUDIT_ACTIONS.LOGIN, resourceType: RESOURCE_TYPES.USER, resourceId: userId });
};

export const logLogout = (practiceId, userId) => {
  return logAudit({ practiceId, userId, action: AUDIT_ACTIONS.LOGOUT, resourceType: RESOURCE_TYPES.USER, resourceId: userId });
};

export const logAccessDenied = (userId, resourceType, resourceId, reason = '') => {
  return logAudit({
    practiceId: 'unknown', userId: userId || 'anonymous',
    action: AUDIT_ACTIONS.ACCESS_DENIED, resourceType, resourceId,
    details: { reason }
  });
};

export const getUserAuditLogs = async (practiceId, maxResults = 100) => {
  if (IS_DEMO) {
    return DEMO_AUDIT_LOGS.slice(0, maxResults);
  }

  try {
    const q = query(
      collection(db, 'auditLogs'),
      where('practiceId', '==', practiceId),
      orderBy('timestamp', 'desc'),
      limit(maxResults)
    );
    const querySnapshot = await getDocs(q);
    const logs = [];
    querySnapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });
    return logs;
  } catch (error) {
    console.error('Failed to fetch audit logs:', error);
    throw error;
  }
};

export const getResourceAuditLogs = async (resourceType, resourceId, maxResults = 50) => {
  if (IS_DEMO) {
    return DEMO_AUDIT_LOGS
      .filter(l => l.resourceType === resourceType && l.resourceId === resourceId)
      .slice(0, maxResults);
  }

  try {
    const q = query(
      collection(db, 'auditLogs'),
      where('resourceType', '==', resourceType),
      where('resourceId', '==', resourceId),
      orderBy('timestamp', 'desc'),
      limit(maxResults)
    );
    const querySnapshot = await getDocs(q);
    const logs = [];
    querySnapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });
    return logs;
  } catch (error) {
    console.error('Failed to fetch resource audit logs:', error);
    throw error;
  }
};

const getHashedIP = async () => {
  try {
    const placeholder = 'client-ip-hash';
    return hash(placeholder);
  } catch (error) {
    return 'unknown';
  }
};

export default {
  logAudit,
  logPatientAccess,
  logCaseAccess,
  logPhotoAccess,
  logPhotoUpload,
  logPhotoDownload,
  logLogin,
  logLogout,
  logAccessDenied,
  getUserAuditLogs,
  getResourceAuditLogs,
  AUDIT_ACTIONS,
  RESOURCE_TYPES
};
